import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface FAQItem {
  question: string;
  answer: string;
  category: 'ai-art' | 'printing' | 'ordering' | 'quality' | 'customization';
}

const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs: FAQItem[] = [
    {
      question: t('faq.questions.whatIsAiArt.question'),
      answer: t('faq.questions.whatIsAiArt.answer'),
      category: 'ai-art'
    },
    {
      question: t('faq.questions.howIsArtCreated.question'),
      answer: t('faq.questions.howIsArtCreated.answer'),
      category: 'ai-art'
    },
    {
      question: t('faq.questions.printQuality.question'),
      answer: t('faq.questions.printQuality.answer'),
      category: 'printing'
    },
    {
      question: t('faq.questions.customOrders.question'),
      answer: t('faq.questions.customOrders.answer'),
      category: 'customization'
    },
    {
      question: t('faq.questions.deliveryTime.question'),
      answer: t('faq.questions.deliveryTime.answer'),
      category: 'ordering'
    },
    {
      question: t('faq.questions.printingSizes.question'),
      answer: t('faq.questions.printingSizes.answer'),
      category: 'printing'
    },
    {
      question: t('faq.questions.artStyles.question'),
      answer: t('faq.questions.artStyles.answer'),
      category: 'ai-art'
    },
    {
      question: t('faq.questions.orderProcess.question'),
      answer: t('faq.questions.orderProcess.answer'),
      category: 'ordering'
    },
    {
      question: t('faq.questions.uniqueness.question'),
      answer: t('faq.questions.uniqueness.answer'),
      category: 'quality'
    },
    {
      question: t('faq.questions.collaboration.question'),
      answer: t('faq.questions.collaboration.answer'),
      category: 'customization'
    }
  ];

  const getCategoryIcon = (category: FAQItem['category']) => {
    const iconClass = "w-5 h-5 text-gold flex-shrink-0";
    switch (category) {
      case 'ai-art':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'printing':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
        );
      case 'ordering':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        );
      case 'quality':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'customization':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        );
    }
  };

  return (
    <section id="faq" className="py-24 bg-dark-secondary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '120px', opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/50 mx-auto mb-8"
          />
          
          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-light text-cream mb-6 tracking-wide">
            {t('faq.title')}
          </h2>
          
          <p className="text-lg text-cream-dark max-w-3xl mx-auto font-light leading-relaxed">
            {t('faq.subtitle')}
          </p>

          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '80px', opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/30 mx-auto mt-8"
          />
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-dark-surface border border-dark-border/20 hover:border-gold/30 transition-all duration-300 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <button
                className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left focus:outline-none focus:ring-2 focus:ring-gold/50"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-content-${index}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-2 sm:gap-4">
                    <div className="hidden sm:block">
                      {getCategoryIcon(faq.category)}
                    </div>
                    <h3 className="font-light text-cream text-sm sm:text-lg leading-relaxed pr-2 sm:pr-8">
                      {faq.question}
                    </h3>
                  </div>
                  <motion.svg
                    className="w-6 h-6 text-gold flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M19 9l-7 7-7-7"
                    />
                  </motion.svg>
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    id={`faq-content-${index}`}
                    className="px-4 sm:px-6 pb-4 sm:pb-6"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div className="ml-2 sm:ml-9 border-l border-gold/20 pl-3 sm:pl-6">
                      <p className="text-cream-dark text-sm sm:text-base leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="bg-dark-surface border border-gold/20 p-6 sm:p-8">
            <h3 className="font-playfair text-xl sm:text-2xl text-cream mb-4">
              {t('faq.stillHaveQuestions')}
            </h3>
            <p className="text-cream-dark text-sm sm:text-base mb-6 leading-relaxed">
              {t('faq.contactUs')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@tamerin.tech?subject=Question%20about%20AI%20Art"
                className="inline-flex items-center px-6 py-3 border border-gold/30 hover:border-gold hover:bg-gold/5 text-cream hover:text-gold transition-all duration-300"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('contact.sendEmail')}
              </a>
              <a
                href="https://wa.me/4915907614478?text=Hi%2C%20I%20have%20a%20question%20about%20your%20AI%20art%20services"
                className="inline-flex items-center px-6 py-3 bg-gold/90 hover:bg-gold text-dark-primary hover:text-dark-primary transition-all duration-300"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.382"/>
                </svg>
                {t('contact.openWhatsApp')}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;